import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  Form,
  Header,
  Card,
  Statistic,
  Message,
} from 'semantic-ui-react';
import { API, showError, showInfo, showSuccess } from '../../helpers';
import { renderQuota } from '../../helpers/render';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../context/User';

/** Same threshold as header MEMBER vs ADMIN (model/user.go RoleAdminUser). */
function isSessionAdmin(user) {
  return typeof user?.role === 'number' && user.role >= 10;
}

const TopUp = () => {
  const { t } = useTranslation();
  const [userState] = useContext(UserContext);
  const [redemptionCode, setRedemptionCode] = useState('');
  const [topUpLink, setTopUpLink] = useState('');
  const [userQuota, setUserQuota] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [user, setUser] = useState({});

  const topUp = async () => {
    if (redemptionCode === '') {
      showInfo(t('topup.redeem_code.empty_code'));
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await API.post('/api/user/topup', {
        key: redemptionCode,
      });
      const { success, message, data } = res.data;
      if (success) {
        showSuccess(t('topup.redeem_code.success'));
        setUserQuota((quota) => {
          return quota + data;
        });
        setRedemptionCode('');
      } else {
        showError(message);
      }
    } catch (err) {
      showError(t('topup.redeem_code.request_failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const openTopUpLink = () => {
    if (!topUpLink) {
      return;
    }
    let url = new URL(topUpLink);
    let username = user.username;
    let user_id = user.id;
    url.searchParams.append('username', username);
    url.searchParams.append('user_id', user_id);
    url.searchParams.append('transaction_id', crypto.randomUUID());
    window.open(url.toString(), '_blank');
  };

  const getUserQuota = async () => {
    let res = await API.get(`/api/user/self`);
    const { success, message, data } = res.data;
    if (success) {
      setUserQuota(data.quota);
      setUser(data);
    } else {
      showError(message);
    }
  };

  useEffect(() => {
    const applyStatus = (status) => {
      if (status?.top_up_link) {
        setTopUpLink(status.top_up_link);
      } else {
        setTopUpLink('');
      }
    };
    try {
      const status = JSON.parse(localStorage.getItem('status') || '{}');
      applyStatus(status);
    } catch {
      setTopUpLink('');
    }
    (async () => {
      try {
        const res = await API.get('/api/status');
        const d = res.data?.data;
        if (res.data?.success && d) {
          applyStatus(d);
          localStorage.setItem('status', JSON.stringify(d));
        }
      } catch {
        // keep cached values
      }
    })();
    getUserQuota().then();
  }, []);

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header>
            <Header as='h2'>{t('topup.title')}</Header>
          </Card.Header>
          <div className='topup-stack'>
            <Message info className='topup-intro-msg'>
              {t('topup.get_code.subtitle')}
            </Message>

            <div className='topup-dual-wrap'>
              <div className='topup-dual-cards'>
              <Card fluid className='chart-card topup-panel'>
                <Card.Content className='topup-panel-content'>
                  <Card.Header style={{ margin: 0 }}>
                    <Header
                      as='h3'
                      style={{ color: '#00ff85', margin: 0 }}
                    >
                      <i className='credit card icon'></i>
                      {t('topup.get_code.title')}
                    </Header>
                  </Card.Header>
                  {isSessionAdmin(userState.user) && !topUpLink && (
                    <Message warning style={{ margin: 0 }}>
                      {t('topup.get_code.no_link_hint_admin')}
                    </Message>
                  )}
                  <Card.Description className='topup-panel-body'>
                    <div className='topup-stat-wrap'>
                      <Statistic size='small'>
                        <Statistic.Value style={{ color: '#00ff85' }}>
                          {renderQuota(userQuota, t)}
                        </Statistic.Value>
                        <Statistic.Label style={{ color: 'rgba(255,255,255,0.72)' }}>
                          {t('topup.get_code.current_quota')}
                        </Statistic.Label>
                      </Statistic>
                    </div>
                    <div className='topup-panel-actions'>
                      <Button
                        primary
                        fluid
                        size='large'
                        onClick={openTopUpLink}
                        disabled={!topUpLink}
                      >
                        {t('topup.get_code.button')}
                      </Button>
                    </div>
                  </Card.Description>
                </Card.Content>
              </Card>

              <Card fluid className='chart-card topup-panel'>
                <Card.Content className='topup-panel-content'>
                  <Card.Header style={{ margin: 0 }}>
                    <Header as='h3' style={{ color: '#00ff85', margin: 0 }}>
                      <i className='ticket alternate icon'></i>
                      {t('topup.redeem_code.title')}
                    </Header>
                  </Card.Header>
                  <Card.Description className='topup-panel-body'>
                    <Form.Input
                      fluid
                      className='topup-redeem-input'
                      icon='key'
                      iconPosition='left'
                      placeholder={t('topup.redeem_code.placeholder')}
                      value={redemptionCode}
                      onChange={(e) => {
                        setRedemptionCode(e.target.value);
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pastedText = e.clipboardData.getData('text');
                        setRedemptionCode(pastedText.trim());
                      }}
                      action={
                        <Button
                          type='button'
                          icon='paste'
                          content={t('topup.redeem_code.paste')}
                          className='topup-paste-btn'
                          onClick={async () => {
                            try {
                              const text =
                                await navigator.clipboard.readText();
                              setRedemptionCode(text.trim());
                            } catch (err) {
                              showError(t('topup.redeem_code.paste_error'));
                            }
                          }}
                        />
                      }
                    />

                    <div className='topup-panel-actions'>
                      <Button
                        color='green'
                        fluid
                        size='large'
                        onClick={topUp}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                      >
                        {isSubmitting
                          ? t('topup.redeem_code.submitting')
                          : t('topup.redeem_code.submit')}
                      </Button>
                    </div>
                  </Card.Description>
                </Card.Content>
              </Card>
              </div>
            </div>
          </div>
        </Card.Content>
      </Card>
    </div>
  );
};

export default TopUp;
